/**
 * Portal shell — the top-level view that composes hero / chart / tiles /
 * registry / modal into a single SPA and mediates every interaction with
 * the API or snapshot clients.
 *
 * Boot flow (task 7.7):
 *   1. `main.ts` calls `loadConfig()` to distinguish authoring vs published.
 *   2. `main.ts` constructs the correct client — `ApiClient` in authoring,
 *      `SnapshotClient` in published — and hands it to `createPortal()`.
 *   3. The shell mounts its DOM once, fetches the initial `Case[]`,
 *      computes `IndexSnapshot` and `TimelinePoint[]` on the client via
 *      `computeIndex` / `buildTimeline`, and renders every child view.
 *   4. In authoring mode the shell wires the upload dropzone / hidden file
 *      input, the header kebab menu (reset, clear-all, publish), and the
 *      per-row edit / delete callbacks emitted by the registry view.
 *   5. In published mode the same DOM omits every mutation affordance
 *      (Requirement 17.2), and a document-level key guard turns any
 *      recognized mutation shortcut into a no-op with a console warning
 *      (Requirement 17.6). Cases whose deck is absent from the snapshot
 *      surface a disabled "Download deck" link (Requirement 16.6).
 *
 * The shell owns the reactive store (`state.ts`) that projects
 * `cases → { index, timeline }` and drives the child views. Views
 * themselves are unaware of the store — the shell drives their `render()`
 * methods directly. That keeps the reactive surface auditable in one file.
 *
 * Requirements: 16.6, 17.1, 17.2, 17.5, 17.6, 17.7.
 */
import type { Case, Config } from "@aim/types";

import { computeIndex } from "../domain/indexCalculator";
import { buildTimeline } from "../domain/timelineBuilder";

import type { ApiClient } from "../clients/apiClient";
import { ApiError, createApiClient } from "../clients/apiClient";
import type { SnapshotClient } from "../clients/snapshotClient";
import {
  SnapshotLoadError,
  createSnapshotClient,
} from "../clients/snapshotClient";

import { createStore, type Store } from "../state";
import { createHeroView, type HeroView } from "./hero";
import { createTilesView, type TilesView } from "./tiles";
import { createChartView, type ChartView } from "./chart";
import { createRegistryView, type RegistryView } from "./registry";
import { createModal, type Modal } from "./modal";

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

/**
 * Runtime data source. In authoring mode this is the HTTP `ApiClient`; in
 * published mode it's the read-only `SnapshotClient`. Discriminated by
 * `mode` so the shell can narrow which affordances to render.
 */
export type PortalDataSource =
  | { mode: "authoring"; apiClient: ApiClient }
  | { mode: "published"; snapshotClient: SnapshotClient };

/** Options accepted by `createPortal`. */
export interface PortalOptions {
  /** DOM root the shell mounts into. Its contents are replaced. */
  root: HTMLElement;
  /** Boot-time config used to gate affordances (Requirement 17). */
  config: Config;
  /** Whichever client the boot code chose to build. */
  dataSource: PortalDataSource;
  /**
   * Injectable store for tests; the default is a fresh in-module store.
   * Production callers ignore this.
   */
  store?: Store;
}

/** Public handle returned by `createPortal`. */
export interface Portal {
  /** Kick off the initial load. Idempotent — safe to call more than once. */
  refresh(): Promise<void>;
  /** Tear down the shell and release every child view. */
  destroy(): void;
  /** Test hook: current store snapshot. */
  getStoreState(): ReturnType<Store["getState"]>;
}

// ---------------------------------------------------------------------------
// Boot helpers used by main.ts
// ---------------------------------------------------------------------------

/**
 * Build the correct client for `config.mode`, then hand it to `createPortal`.
 * Kept as a small helper so `main.ts` reads top-to-bottom without branching.
 */
export function bootDataSource(config: Config): PortalDataSource {
  // Requirement 17.1: only the correct client is instantiated per mode; the
  // other one is never even referenced.
  if (config.mode === "authoring") {
    return { mode: "authoring", apiClient: createApiClient({ apiBase: config.apiBase }) };
  }
  return {
    mode: "published",
    snapshotClient: createSnapshotClient({ snapshotUrl: config.snapshotUrl }),
  };
}

// ---------------------------------------------------------------------------
// DOM builders
// ---------------------------------------------------------------------------

/**
 * References into the shell DOM tree so re-renders and mode toggles can
 * hit specific nodes without re-querying the document.
 */
interface ShellRefs {
  root: HTMLElement;
  header: HTMLElement;
  uploadBtn: HTMLButtonElement | null;
  menuBtn: HTMLButtonElement | null;
  menu: HTMLElement | null;
  resetBtn: HTMLButtonElement | null;
  clearBtn: HTMLButtonElement | null;
  publishBtn: HTMLButtonElement | null;
  fileInput: HTMLInputElement | null;
  dropzone: HTMLElement | null;
  heroMount: HTMLElement;
  chartMount: HTMLElement;
  tilesMount: HTMLElement;
  registryHead: HTMLElement;
  registryCount: HTMLElement;
  registryMount: HTMLElement;
  modalMount: HTMLElement;
  toast: HTMLElement;
  loadErrorBanner: HTMLElement;
}

/** Build the shell DOM once. Mode-dependent affordances are wired later. */
function buildShell(root: HTMLElement, mode: Config["mode"]): ShellRefs {
  root.replaceChildren();

  // -- header --------------------------------------------------------------
  const header = document.createElement("header");
  header.className = "portal-header";

  const brand = document.createElement("div");
  brand.className = "brand";
  const brandBody = document.createElement("div");
  brandBody.className = "brand-body";
  const h1 = document.createElement("h1");
  h1.textContent = "AI Maturity Index";
  const sub = document.createElement("div");
  sub.className = "sub";
  sub.textContent =
    mode === "published" ? "Published snapshot" : "Use case intake portal";
  brandBody.append(h1, sub);
  brand.append(brandBody);

  const headActions = document.createElement("div");
  headActions.className = "head-actions";

  let uploadBtn: HTMLButtonElement | null = null;
  let menuBtn: HTMLButtonElement | null = null;
  let menu: HTMLElement | null = null;
  let resetBtn: HTMLButtonElement | null = null;
  let clearBtn: HTMLButtonElement | null = null;
  let publishBtn: HTMLButtonElement | null = null;
  let fileInput: HTMLInputElement | null = null;
  let dropzone: HTMLElement | null = null;

  if (mode === "authoring") {
    // Requirement 17.7: authoring mode restores the upload button, kebab
    // menu (reset / clear / publish), and drag-and-drop dropzone.
    uploadBtn = document.createElement("button");
    uploadBtn.type = "button";
    uploadBtn.className = "btn primary upload-btn";
    uploadBtn.textContent = "Upload use case";

    menuBtn = document.createElement("button");
    menuBtn.type = "button";
    menuBtn.className = "icon-btn menu-btn";
    menuBtn.setAttribute("aria-label", "Data options");
    menuBtn.setAttribute("aria-haspopup", "true");
    menuBtn.setAttribute("aria-expanded", "false");
    menuBtn.textContent = "⋮";

    menu = document.createElement("div");
    menu.className = "menu";
    menu.hidden = true;

    resetBtn = document.createElement("button");
    resetBtn.type = "button";
    resetBtn.className = "menu-item";
    resetBtn.textContent = "Reset to baseline";

    clearBtn = document.createElement("button");
    clearBtn.type = "button";
    clearBtn.className = "menu-item danger";
    clearBtn.textContent = "Clear all data";

    publishBtn = document.createElement("button");
    publishBtn.type = "button";
    publishBtn.className = "menu-item";
    publishBtn.textContent = "Publish snapshot";

    menu.append(resetBtn, clearBtn, publishBtn);
    headActions.append(uploadBtn, menuBtn, menu);
  } else {
    // Published mode surfaces the `publishedAt` timestamp as a passive
    // indicator instead of any interactive control (Requirement 17.2).
    const pubStamp = document.createElement("div");
    pubStamp.className = "published-stamp";
    pubStamp.textContent = "Read-only snapshot";
    headActions.append(pubStamp);
  }

  header.append(brand, headActions);

  // -- main ----------------------------------------------------------------
  const main = document.createElement("main");
  main.className = "portal-main";

  const heroWrap = document.createElement("div");
  heroWrap.className = "hero-wrap";

  const heroMount = document.createElement("div");
  heroMount.className = "hero-mount";
  const chartMount = document.createElement("div");
  chartMount.className = "chart-wrap chart-mount";
  heroWrap.append(heroMount, chartMount);

  const tilesMount = document.createElement("div");
  tilesMount.className = "tiles-mount";

  const registrySection = document.createElement("section");
  registrySection.className = "registry-section";

  const registryHead = document.createElement("div");
  registryHead.className = "reg-head";
  const regTitle = document.createElement("h2");
  regTitle.textContent = "Use case registry";
  const registryCount = document.createElement("span");
  registryCount.className = "count";
  registryCount.textContent = "";
  registryHead.append(regTitle, registryCount);

  const registryMount = document.createElement("div");
  registryMount.className = "registry-mount";

  registrySection.append(registryHead, registryMount);

  // Authoring-only drop target overlays the whole main so an analyst can
  // drop a .pptx anywhere on the page (Requirement 17.7). In published
  // mode the dropzone is not created at all — the DOM has no upload
  // affordance (Requirement 17.2).
  if (mode === "authoring") {
    dropzone = document.createElement("div");
    dropzone.className = "dropzone-overlay";
    dropzone.hidden = true;
    const dzInner = document.createElement("div");
    dzInner.className = "dropzone-inner";
    dzInner.textContent = "Drop a .pptx file to intake a case";
    dropzone.append(dzInner);

    fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.className = "file-input";
    fileInput.accept =
      ".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation";
    fileInput.hidden = true;
  }

  const loadErrorBanner = document.createElement("div");
  loadErrorBanner.className = "load-error-banner";
  loadErrorBanner.setAttribute("role", "alert");
  loadErrorBanner.hidden = true;

  main.append(loadErrorBanner, heroWrap, tilesMount, registrySection);

  // -- modal + toast overlays ---------------------------------------------
  const modalMount = document.createElement("div");
  modalMount.className = "modal-mount";

  const toast = document.createElement("div");
  toast.className = "toast";
  toast.setAttribute("role", "status");
  toast.setAttribute("aria-live", "polite");
  toast.hidden = true;

  const overlays = document.createElement("div");
  overlays.className = "portal-overlays";
  overlays.append(modalMount, toast);
  if (dropzone !== null) overlays.append(dropzone);
  if (fileInput !== null) overlays.append(fileInput);

  root.append(header, main, overlays);

  return {
    root,
    header,
    uploadBtn,
    menuBtn,
    menu,
    resetBtn,
    clearBtn,
    publishBtn,
    fileInput,
    dropzone,
    heroMount,
    chartMount,
    tilesMount,
    registryHead,
    registryCount,
    registryMount,
    modalMount,
    toast,
    loadErrorBanner,
  };
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/** Show a transient toast; auto-hides after 3.5s. */
function showToast(el: HTMLElement, message: string): void {
  el.textContent = message;
  el.hidden = false;
  el.classList.add("show");
  window.setTimeout(() => {
    el.classList.remove("show");
    el.hidden = true;
  }, 3500);
}

/** Format the "N cases" label used in the registry heading. */
function formatCaseCount(n: number): string {
  return `${String(n)} case${n === 1 ? "" : "s"}`;
}

/**
 * Coerce anything thrown by an API call into a display string. `ApiError`
 * envelopes are unwrapped so the analyst sees the server's own message.
 */
function describeError(cause: unknown): string {
  if (cause instanceof ApiError) return `${cause.code}: ${cause.envelope.message}`;
  if (cause instanceof SnapshotLoadError) return `${cause.code}: ${cause.message}`;
  if (cause instanceof Error) return cause.message;
  return String(cause);
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Build the portal shell, wire every child view, and return a controller.
 *
 * The factory is synchronous — the initial network round-trip is kicked
 * off by `refresh()` (which `main.ts` awaits). That separation keeps the
 * DOM ready to show a load-error banner immediately if boot fails,
 * without racing against the child views' first render.
 */
export function createPortal(opts: PortalOptions): Portal {
  const { root, config, dataSource } = opts;
  const store = opts.store ?? createStore();

  const refs = buildShell(root, config.mode);

  // -- child views ---------------------------------------------------------
  const hero: HeroView = createHeroView();
  hero.mount(refs.heroMount);

  const tiles: TilesView = createTilesView();
  tiles.mount(refs.tilesMount);

  const chart: ChartView = createChartView(refs.chartMount);
  chart.onSelect((caseId) => {
    // Selection surface is left as a scroll-into-view convenience for now.
    // A dedicated case-detail drawer is out of scope for task 7.7.
    const row = refs.registryMount.querySelector<HTMLElement>(
      `.row[data-case-id="${caseId}"]`,
    );
    if (row !== null) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
      row.classList.add("row-selected");
      window.setTimeout(() => {
        row.classList.remove("row-selected");
      }, 1500);
    }
  });

  const registry: RegistryView = createRegistryView();
  registry.mount(refs.registryMount, {
    mode: config.mode,
    // Requirement 17.2: edit / delete callbacks are simply never wired in
    // published mode. The registry view already omits the buttons; the
    // guard here is belt-and-braces so a future refactor cannot regress.
    onEdit:
      config.mode === "authoring" && dataSource.mode === "authoring"
        ? (id) => {
            void handleEdit(id);
          }
        : undefined,
    onDelete:
      config.mode === "authoring" && dataSource.mode === "authoring"
        ? (id) => {
            void handleDelete(id);
          }
        : undefined,
  });

  // Modal is authoring-only. The DOM node exists in both modes so future
  // published-mode drawers can reuse the container, but no `Modal`
  // controller is instantiated when there's no `ApiClient` (Requirement
  // 17.1: only the correct client is referenced).
  const modal: Modal | null =
    dataSource.mode === "authoring"
      ? createModal(refs.modalMount, dataSource.apiClient)
      : null;

  // -- authoring wiring ----------------------------------------------------
  // The handlers close over `dataSource` narrowed to `authoring`; they are
  // never invoked in published mode because the affordances that reach
  // them are never rendered.
  async function handleUpload(file: File): Promise<void> {
    if (dataSource.mode !== "authoring" || modal === null) return;
    try {
      const extract = await dataSource.apiClient.parseUpload(file);
      const persisted = await modal.open(extract, { deckFileName: file.name });
      if (persisted !== null) {
        showToast(refs.toast, `Added ${persisted.title}`);
        await refresh();
      }
    } catch (cause) {
      showToast(refs.toast, `Parse failed — ${describeError(cause)}`);
    }
  }

  async function handleEdit(caseId: string): Promise<void> {
    if (dataSource.mode !== "authoring") return;
    // A dedicated edit modal is future work (task 7.6+). For now we
    // surface a prompt-driven title rename so the wire path is exercised
    // end-to-end. `patchCase` is the API's canonical update endpoint.
    const current = store
      .getState()
      .cases.find((c) => c.id === caseId);
    if (current === undefined) return;
    const nextTitle = window.prompt("New title", current.title);
    if (nextTitle === null) return;
    const trimmed = nextTitle.trim();
    if (trimmed.length === 0 || trimmed === current.title) return;
    try {
      await dataSource.apiClient.patchCase(caseId, { title: trimmed });
      await refresh();
    } catch (cause) {
      showToast(refs.toast, `Edit failed — ${describeError(cause)}`);
    }
  }

  async function handleDelete(caseId: string): Promise<void> {
    if (dataSource.mode !== "authoring") return;
    // The registry view already gated on `confirm()` before invoking us.
    try {
      await dataSource.apiClient.deleteCase(caseId);
      await refresh();
    } catch (cause) {
      showToast(refs.toast, `Delete failed — ${describeError(cause)}`);
    }
  }

  async function handleResetBaseline(): Promise<void> {
    if (dataSource.mode !== "authoring") return;
    const ok = window.confirm(
      "Reset the portfolio to the packaged baseline? All non-baseline cases will be removed.",
    );
    if (!ok) return;
    try {
      const res = await dataSource.apiClient.resetBaseline();
      showToast(refs.toast, `Reset complete — ${String(res.removedFiles)} deck(s) removed.`);
      await refresh();
    } catch (cause) {
      showToast(refs.toast, `Reset failed — ${describeError(cause)}`);
    }
  }

  async function handleClearAll(): Promise<void> {
    if (dataSource.mode !== "authoring") return;
    const ok = window.confirm(
      "Delete every case and its stored deck? This cannot be undone.",
    );
    if (!ok) return;
    try {
      const res = await dataSource.apiClient.clearAll();
      showToast(refs.toast, `Cleared — ${String(res.removedCases)} case(s) removed.`);
      await refresh();
    } catch (cause) {
      showToast(refs.toast, `Clear failed — ${describeError(cause)}`);
    }
  }

  async function handlePublish(): Promise<void> {
    if (dataSource.mode !== "authoring") return;
    try {
      const res = await dataSource.apiClient.publish();
      showToast(
        refs.toast,
        `Published ${String(res.caseCount)} case(s) → ${res.path}`,
      );
    } catch (cause) {
      showToast(refs.toast, `Publish failed — ${describeError(cause)}`);
    }
  }

  const authoringListeners: Array<() => void> = [];
  function addListener(
    el: EventTarget,
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: AddEventListenerOptions,
  ): void {
    el.addEventListener(type, listener, options);
    authoringListeners.push(() => {
      el.removeEventListener(type, listener, options);
    });
  }

  if (config.mode === "authoring") {
    // Upload button opens the hidden file input, which then feeds `handleUpload`.
    if (refs.uploadBtn !== null && refs.fileInput !== null) {
      const btn = refs.uploadBtn;
      const input = refs.fileInput;
      addListener(btn, "click", () => {
        input.value = "";
        input.click();
      });
      addListener(input, "change", () => {
        const file = input.files?.[0];
        if (file !== undefined) void handleUpload(file);
      });
    }

    // Kebab menu open/close + item handlers.
    if (refs.menuBtn !== null && refs.menu !== null) {
      const btn = refs.menuBtn;
      const menu = refs.menu;
      const closeMenu = (): void => {
        menu.hidden = true;
        btn.setAttribute("aria-expanded", "false");
      };
      addListener(btn, "click", (ev) => {
        ev.stopPropagation();
        const open = menu.hidden;
        menu.hidden = !open;
        btn.setAttribute("aria-expanded", String(open));
      });
      // Any click outside the menu closes it.
      addListener(document, "click", (ev) => {
        if (menu.hidden) return;
        const target = ev.target as Node | null;
        if (target !== null && !menu.contains(target) && target !== btn) {
          closeMenu();
        }
      });
      addListener(document, "keydown", (ev) => {
        if ((ev as KeyboardEvent).key === "Escape") closeMenu();
      });
    }
    if (refs.resetBtn !== null) {
      addListener(refs.resetBtn, "click", () => {
        if (refs.menu !== null) refs.menu.hidden = true;
        void handleResetBaseline();
      });
    }
    if (refs.clearBtn !== null) {
      addListener(refs.clearBtn, "click", () => {
        if (refs.menu !== null) refs.menu.hidden = true;
        void handleClearAll();
      });
    }
    if (refs.publishBtn !== null) {
      addListener(refs.publishBtn, "click", () => {
        if (refs.menu !== null) refs.menu.hidden = true;
        void handlePublish();
      });
    }

    // Drag-and-drop over the whole document. `dragover` must call
    // `preventDefault()` for `drop` to fire in browsers.
    if (refs.dropzone !== null) {
      const dz = refs.dropzone;
      let dragDepth = 0;
      addListener(document, "dragenter", (ev) => {
        const e = ev as DragEvent;
        if (!e.dataTransfer?.types.includes("Files")) return;
        e.preventDefault();
        dragDepth += 1;
        dz.hidden = false;
      });
      addListener(document, "dragleave", () => {
        dragDepth = Math.max(0, dragDepth - 1);
        if (dragDepth === 0) dz.hidden = true;
      });
      addListener(document, "dragover", (ev) => {
        (ev as DragEvent).preventDefault();
      });
      addListener(document, "drop", (ev) => {
        const e = ev as DragEvent;
        e.preventDefault();
        dragDepth = 0;
        dz.hidden = true;
        const file = e.dataTransfer?.files?.[0];
        if (file !== undefined) void handleUpload(file);
      });
    }
  } else {
    // Requirement 17.6: in published mode, any keyboard-shortcut-triggered
    // mutation is a no-op that logs a console warning. The concrete set
    // we intercept today is small — Delete/Backspace while a case row is
    // focused — but the guard is written in terms of "attempted mutation
    // action", so extending it later stays local to this listener.
    addListener(document, "keydown", (ev) => {
      const e = ev as KeyboardEvent;
      const isDeleteKey = e.key === "Delete" || e.key === "Backspace";
      if (!isDeleteKey) return;
      const target = e.target as Element | null;
      if (target === null) return;
      // Ignore keys inside editable form controls to keep native form UX
      // untouched (there are no forms in published mode today, but this
      // stays correct if one is ever added).
      const isEditable =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target as HTMLElement).isContentEditable === true;
      if (isEditable) return;
      const row = target.closest<HTMLElement>(".row[data-case-id]");
      if (row === null) return;
      e.preventDefault();
      // eslint-disable-next-line no-console -- intentional per Requirement 17.6
      console.warn(
        "[portal] Mutation shortcut ignored: portal is running in published (read-only) mode.",
        { caseId: row.dataset.caseId, key: e.key },
      );
    });
  }

  // -- store fan-out -------------------------------------------------------
  // A single subscription drives every child view. Views are cheap to
  // re-render — the whole payload is a `Case[]` plus derived snapshots.
  const unsubscribe = store.subscribe((s) => {
    hero.render(s.index);
    tiles.render(s.index);
    chart.render(s.timeline);
    registry.render(s.cases);
    refs.registryCount.textContent = formatCaseCount(s.cases.length);
    if (s.loadError !== null) {
      refs.loadErrorBanner.hidden = false;
      refs.loadErrorBanner.textContent = s.loadError;
    } else {
      refs.loadErrorBanner.hidden = true;
      refs.loadErrorBanner.textContent = "";
    }
  });

  // Prime the child views with the initial (empty) store state so the
  // shell shows placeholders instead of an empty page while the first
  // fetch is in flight.
  hero.render(null);
  tiles.render(null);
  chart.render([]);
  registry.render([]);
  refs.registryCount.textContent = formatCaseCount(0);

  // -- refresh -------------------------------------------------------------
  async function loadCases(): Promise<readonly Case[]> {
    if (dataSource.mode === "authoring") {
      return dataSource.apiClient.listCases();
    }
    return dataSource.snapshotClient.listCases();
  }

  async function refresh(): Promise<void> {
    try {
      const cases = await loadCases();
      // `computeIndex` and `buildTimeline` are pure — computing them on
      // the client keeps the reactive projection auditable in one place.
      const index = computeIndex(cases);
      const timeline = buildTimeline(cases);
      store.set({ cases, index, timeline, loadError: null });
    } catch (cause) {
      // Requirement 17.4 (published) / general error surfacing (authoring):
      // expose the empty `Case[]` and surface the error through the
      // reactive store so the load-error banner renders.
      const message =
        dataSource.mode === "authoring"
          ? `Failed to load cases — ${describeError(cause)}`
          : `Failed to load snapshot — ${describeError(cause)}`;
      store.set({
        cases: [],
        index: computeIndex([]),
        timeline: [],
        loadError: message,
      });
    }
  }

  return {
    refresh,
    getStoreState(): ReturnType<Store["getState"]> {
      return store.getState();
    },
    destroy(): void {
      for (const off of authoringListeners) off();
      authoringListeners.length = 0;
      unsubscribe();
      hero.destroy();
      tiles.destroy();
      chart.destroy();
      registry.destroy();
      if (modal !== null) modal.destroy();
      root.replaceChildren();
    },
  };
}
